import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../a11y/usePageTitle';
import { brand } from '../brand/brand';

/*
 * The written pages: how it works, FAQ, support, terms, privacy.
 *
 * PROVENANCE. The terms and privacy wording is adapted from BuzlloPay's
 * web/src/routes/LegalPage.tsx, which carries this note at the top of the file and
 * still applies here:
 *
 *     "The copy below is a STARTER TEMPLATE only — it is NOT legal advice and must
 *      be reviewed and replaced by a qualified lawyer for your jurisdiction."
 *
 * WHAT DID NOT SURVIVE THE COPY, and why it matters. BuzlloPay's terms address a
 * BUSINESS using a platform to take orders. These address a CONSUMER buying
 * prepaid credit. Almost every clause changes person, and several have no Buzllo
 * equivalent at all — expiry, unspent balance, who honours the voucher, and what
 * happens if a business closes. Those sections are marked REVIEW below because
 * they are the ones with actual regulatory weight: prepaid instruments are
 * regulated per US state, and rules on expiry dates and unclaimed balances differ
 * between them.
 *
 * The SMS paragraph is reproduced VERBATIM from BuzlloPay. Those sentences are
 * required word-for-word by TCR and the carriers, who search for them as literal
 * strings; paraphrasing them is one of the things that got that campaign rejected.
 */

const UPDATED = 'October 2026';

function Doc({ title, children }: { title: string; children: ReactNode }) {
  usePageTitle(title);

  return (
    <article className="doc">
      <header className="stack" style={{ gap: 6 }}>
        <h1>{title}</h1>
        <p className="faint" style={{ margin: 0 }}>Last updated: {UPDATED}</p>
      </header>

      <div className="stack" style={{ gap: 22 }}>{children}</div>

      <nav aria-label="Related" className="doc-nav">
        <Link to="/terms">Terms</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/support">Support</Link>
        <Link to="/faq">FAQ</Link>
      </nav>
    </article>
  );
}

function S({ h, children }: { h: string; children: ReactNode }) {
  return (
    <section className="stack" style={{ gap: 8 }}>
      <h2>{h}</h2>
      {children}
    </section>
  );
}

/**
 * A clause a lawyer must confirm before launch.
 *
 * Visible in DEVELOPMENT, so nobody forgets it is there - and hidden from the
 * production build, where a yellow "Needs legal review" box on the public FAQ and
 * Terms tells every customer the terms are unfinished. The list still lives in this
 * file; it is the lawyer's checklist, not the customer's.
 */
function Review({ children }: { children: ReactNode }) {
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <p className="notice review">
      <strong>Needs legal review:</strong> {children}
    </p>
  );
}

/* ========================================================== how it works === */

export function HowItWorks() {
  return (
    <Doc title="How it works">
      <p className="lead">
        {brand.name} sells prepaid credit at local businesses. You pay less than the
        credit is worth, and spend it with them like cash.
      </p>

      <S h="1. Find an offer">
        <p className="muted">
          Every offer shows what you pay and what you get. A $50 voucher for $40 means
          $50 of credit at that business for $40 today.
        </p>
      </S>

      <S h="2. Buy it">
        <p className="muted">
          Pay by card. Your card details go straight to our payment provider and never
          touch our servers. The voucher lands in your wallet within a few seconds and
          we email you a copy.
        </p>
      </S>

      <S h="3. Spend it">
        <p className="muted">
          Show the code on your phone at the counter. Nothing to print. You do not have
          to spend it all at once — whatever is left stays on the voucher until it
          expires, and your wallet shows the balance.
        </p>
      </S>

      <S h="Who you are buying from">
        <p className="muted">
          The business is the seller and the one who honours the voucher.{' '}
          {brand.name} runs the platform, takes the payment and issues the credit.
          If something goes wrong at the counter, talk to the business first — they can
          usually fix it on the spot. If they cannot, we can.
        </p>
      </S>
    </Doc>
  );
}

/* ================================================================== FAQ === */

export function Faq() {
  return (
    <Doc title="Frequently asked questions">
      <S h="Do I have to spend it all in one visit?">
        <p className="muted">
          No. Vouchers hold a balance. Spend part of it, and the rest stays on the
          voucher for next time. Your wallet always shows what is left.
        </p>
      </S>

      <S h="Can I buy more than one?">
        <p className="muted">
          Usually yes. Some offers set a limit per customer, and the offer page says so
          before you pay. Choose the quantity at checkout.
        </p>
      </S>

      <S h="When does it expire?">
        <p className="muted">
          Each offer shows its own expiry date, on the offer page and on the voucher
          itself. We email you before it runs out.
        </p>
        <Review>
          Expiry on prepaid instruments is regulated, and the minimum permitted period
          differs by state. Confirm the shortest expiry the platform allows is lawful
          in every state where businesses will sell.
        </Review>
      </S>

      <S h="What if I do not use it?">
        <p className="muted">
          Unspent credit stays on the voucher until the expiry date shown.
        </p>
        <Review>
          What happens to a balance after expiry — whether it is forfeited, refundable,
          or must be escheated to the state as unclaimed property — is the clause most
          likely to be wrong here. It needs a definitive answer per state before launch.
        </Review>
      </S>

      <S h="Can I get a refund?">
        <p className="muted">
          If a voucher has not been used at all, contact us and we will look at it. Once
          it has been partly spent it cannot be refunded. If a business stops trading or
          refuses to honour a valid voucher, tell us — that is our problem to sort out,
          not yours.
        </p>
        <Review>
          Confirm the refund window, and who bears the cost when a business fails while
          holding unredeemed liability.
        </Review>
      </S>

      <S h="Can I give it to someone else?">
        <p className="muted">
          The voucher is tied to your account, and the code is what proves it. Anyone
          holding the code can spend it, so treat it like cash and do not post it
          anywhere public.
        </p>
      </S>

      <S h="Is my card safe?">
        <p className="muted">
          We never see or store your card number. Payment is handled by Stripe, and the
          card fields on our checkout are Stripe's own, running in their frame.
        </p>
      </S>

      <S h="Something went wrong">
        <p className="muted">
          Email <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a> with
          your order code and we will reply within one business day.
        </p>
      </S>
    </Doc>
  );
}

/* ============================================================== support === */

export function Support() {
  return (
    <Doc title="Support">
      <p className="lead">
        {brand.name} is operated by {brand.legalEntity}
        {brand.operatorAddress.length > 0 ? `, ${brand.operatorAddress}` : ''}. If you
        need help with an order, a payment or your account, email us and a person
        will reply.
      </p>

      <S h="Contact us">
        {/* THE SIGNED-IN ROUTE FIRST, and the mailto kept below it rather than
            replaced. A ticket gives both sides a record and a status; email gives
            the customer nothing but hope. But somebody who cannot sign in — which
            is itself a reason to contact support — still needs a way through, so
            the address stays. */}
        <p className="muted">
          If you have an account, <Link to="/help">send us a message</Link>. You will
          see our reply in the same place, so nothing gets lost in a spam folder.
        </p>
        <p className="muted">
          Otherwise, email{' '}
          <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>. Either
          way we reply <strong>{brand.supportReplyTime}</strong>.
        </p>
      </S>

      <S h="A problem with a voucher at the counter">
        <p className="muted">
          The business sets its own opening hours, menu and service. For anything about
          a specific visit, the fastest route is the business itself. If they will not
          honour a valid voucher, or the problem is with the payment rather than the
          visit, email us and include the order code from your confirmation so we can
          find it.
        </p>
      </S>

      <S h="Getting your money back">
        <p className="muted">
          Tell us what happened and we will look at it. An unused voucher is
          straightforward. A partly spent one is harder, and one where the business has
          closed is ours to resolve rather than yours to chase.
        </p>
      </S>
    </Doc>
  );
}

/* ================================================================ terms === */

export function Terms() {
  return (
    <Doc title="Terms of Service">
      <p>
        These Terms govern your use of {brand.name} (the “Platform”), operated by{' '}
        {brand.legalEntity}, which lets you buy prepaid credit — “vouchers” — for use
        at participating businesses. By creating an account or buying a voucher you
        agree to these Terms.
      </p>

      <S h="1. What we do, and what we do not">
        <p className="muted">
          {brand.name} sells vouchers on behalf of the businesses listed and issues the
          credit. The BUSINESS is the seller of the goods and services you eventually
          buy with that credit, and the party that honours the voucher. We are not the
          restaurant, the salon or the shop, and we do not control their prices,
          opening hours, menu or quality.
        </p>
      </S>

      <S h="2. Your account">
        <p className="muted">
          You sign in with a one-time code sent to your email. Keep access to that
          mailbox secure — anyone who can read it can reach your vouchers. You must be
          old enough to enter into a contract where you live.
        </p>
      </S>

      <S h="3. Payments">
        <p className="muted">
          Card payments are processed by Stripe. We never receive or store your card
          number. The amount shown at checkout is the amount charged; the credit you
          receive is the face value shown on the offer.
        </p>
      </S>

      <S h="4. Vouchers, balances and expiry">
        <p className="muted">
          A voucher holds a balance which reduces as you spend it. Partial spending is
          allowed. Each voucher carries an expiry date, shown before you buy and on the
          voucher itself. A voucher is not legal tender, cannot be exchanged for cash,
          and is only redeemable with the business that issued it.
        </p>
        <Review>
          This section has no BuzlloPay equivalent — it was written for this product.
          Expiry periods, whether cash redemption can lawfully be refused, and the
          treatment of residual balances are all regulated per state and must be
          confirmed before launch.
        </Review>
      </S>

      <S h="5. Refunds and cancellations">
        <p className="muted">
          An unused voucher may be refunded at our discretion on request. Once any part
          of the balance has been spent, the voucher cannot be refunded. If a business
          ceases trading or refuses to honour a valid voucher, contact us.
        </p>
        <Review>
          Confirm the cooling-off period required by law, and who carries the liability
          when a business fails holding unredeemed vouchers.
        </Review>
      </S>

      <S h="6. Acceptable use">
        <p className="muted">
          You may not use the Platform for unlawful or fraudulent activity, buy vouchers
          for resale, or attempt to redeem a voucher you did not buy. We may suspend
          accounts that breach these Terms and cancel vouchers obtained fraudulently.
        </p>
      </S>

      <S h="7. Disclaimers and liability">
        <p className="muted">
          The Platform is provided “as is”. To the extent permitted by law,{' '}
          {brand.legalEntity} is not liable for indirect or consequential losses, and
          total liability is limited to the amount you paid for the voucher concerned.
        </p>
      </S>

      <S h="8. Changes and termination">
        <p className="muted">
          We may update these Terms; continued use means acceptance. Changes will not
          reduce the value or extend the expiry of a voucher you already hold.
        </p>
      </S>

      <S h="9. Text messages (SMS)">
        <p className="muted">
          {brand.name} offers a recurring text-message program, &ldquo;{brand.name} Text
          Alerts&rdquo;, for people who opt in on our website or by texting a keyword to our
          number. Messages are marketing: the deal of the week at local businesses, and a
          reminder when you have credit left on a voucher. Consent to receive marketing
          texts is not a condition of any purchase. Message frequency varies, up to 4
          messages a month. Message and data rates may apply. Reply STOP to cancel at any
          time, or HELP for help; you can also email{' '}
          <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>. Carriers are
          not liable for delayed or undelivered messages. Separately, one-time
          verification codes and voucher confirmations are sent by text when you ask for
          them, whether or not you are in the marketing program.
        </p>
        <p className="muted">
          See <Link to="/sms">Deals by text</Link> to opt in, and our{' '}
          <Link to="/privacy">Privacy Policy</Link> for how your number is handled.
        </p>
      </S>

      <S h="10. Contact">
        <p className="muted">
          Questions about these Terms:{' '}
          <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>.
        </p>
      </S>
    </Doc>
  );
}

/* ============================================================== privacy === */

export function Privacy() {
  return (
    <Doc title="Privacy Policy">
      <p>
        This Policy explains how {brand.legalEntity} collects and uses personal
        information when you use {brand.name}. The businesses you buy vouchers from see
        only what they need to honour them.
      </p>

      <S h="1. What we collect">
        <p className="muted">
          Your email address, the vouchers you buy and their balances, and the record of
          where and when they were redeemed. Payment card data is handled by Stripe and
          never stored by us.
        </p>
      </S>

      <S h="2. How we use it">
        <p className="muted">
          To issue and honour your vouchers, send transactional messages about them,
          prevent fraud, provide support, and meet our legal and accounting obligations.
        </p>
      </S>

      <S h="3. Sharing">
        <p className="muted">
          With processors that make the service work — Stripe for payments, our email
          provider for notifications — and with the business whose voucher you are
          redeeming, so they can verify it. We do not sell personal data.
        </p>
      </S>

      <S h="4. Messaging">
        <p className="muted">
          You receive transactional messages about your vouchers: sign-in codes,
          purchase confirmations, and a reminder before one expires. Marketing messages
          are sent only where you have opted in, and you can opt out at any time.
        </p>
      </S>

      <S h="5. SMS / Text Messaging">
        {/* REQUIRED VERBATIM by TCR / the carriers, who search for these as literal
            strings. Do not paraphrase, do not split across elements, and do not wrap
            any part in <b>/<em> — inline tags break a text search across the
            sentence and the campaign gets rejected. Reproduced from BuzlloPay's
            LegalPage.tsx, where a paraphrase caused rejections 30896 / 30917. */}
        <p className="muted">Mobile information will not be shared with or sold to third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with any third parties.</p>

        <p className="muted">
          If you opt in to text messages (or request a one-time verification code),{' '}
          {brand.name} sends SMS such as verification codes and voucher confirmations.
          If you opt in to &ldquo;{brand.name} Text Alerts&rdquo; (on our{' '}
          <Link to="/sms">Deals by text</Link> page or by texting a keyword to our number),
          you also receive marketing texts: the deal of the week and balance reminders.
          Consent to receive SMS is not a condition of any purchase. Message frequency
          varies, up to 4 marketing messages a month. Msg &amp; data rates may apply.
          Reply STOP to opt out, HELP for help. For support, contact{' '}
          <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>.
        </p>
        <p className="muted">
          When you opt in we keep your number, the wording you agreed to, the date and
          time, the page you were on and your IP address, so that we can show your
          consent if asked. Replying STOP is recorded the same way.
        </p>
      </S>

      <S h="6. Retention and your rights">
        <p className="muted">
          We keep data as long as needed to honour outstanding vouchers and meet legal
          obligations. Subject to your local law, you may request access, correction or
          deletion of your data. Note that deleting your account does not refund
          unspent vouchers — ask us about those first.
        </p>
      </S>

      <S h="7. Contact">
        <p className="muted">
          Privacy questions or requests:{' '}
          <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>.
        </p>
      </S>
    </Doc>
  );
}
